import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Empty, Skeleton, Typography, message } from 'antd';
import logger from '../../../utils/logger';
import { emitFrontendTelemetry } from '../../../utils/frontendTelemetry';
import {
    getPreferences,
    saveCategoryPreference,
    saveEventOverride,
    resetEventOverride,
} from './api';
import ChannelAvailabilityBanner from './ChannelAvailabilityBanner';
import CategoryCard from './CategoryCard';
import './NotificationPreferences.scss';

const { Title, Paragraph } = Typography;

const extractErrorMessage = (err, fallback) =>
    err?.response?.data?.message ||
    err?.response?.data?.data?.message ||
    err?.message ||
    fallback;

const NotificationPreferences = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [initialLoadError, setInitialLoadError] = useState(null);

    const fetchPreferences = useCallback(async ({ initial = false } = {}) => {
        try {
            const result = await getPreferences();
            setData(result);
            if (initial) setInitialLoadError(null);
        } catch (err) {
            logger.error('Failed to load notification preferences:', err);
            const msg = extractErrorMessage(err, 'Failed to load notification preferences');
            if (initial) {
                setInitialLoadError(msg);
            } else {
                // Refetch failure mid-session: keep the existing data so the user
                // doesn't lose context, just toast.
                message.error('Could not refresh preferences. Reload the page to retry.');
            }
        } finally {
            if (initial) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPreferences({ initial: true });
    }, [fetchPreferences]);

    const runSave = useCallback(
        async (label, telemetryAction, fn) => {
            setSaving(true);
            try {
                await fn();
                message.success(`${label} saved.`);
                void emitFrontendTelemetry('frontend.notification_preferences.saved', {
                    area: 'notification_preferences',
                    action: telemetryAction,
                });
                await fetchPreferences();
            } catch (err) {
                logger.error(`Failed to save ${label.toLowerCase()}:`, err);
                message.error(extractErrorMessage(err, `Failed to save ${label.toLowerCase()}`));
            } finally {
                setSaving(false);
            }
        },
        [fetchPreferences],
    );

    const handleCategorySave = useCallback(
        (categoryKey, body) =>
            runSave('Category preference', 'save_category', () =>
                saveCategoryPreference(categoryKey, body),
            ),
        [runSave],
    );

    const handleEventSave = useCallback(
        (eventKey, body) =>
            runSave('Event override', 'save_event_override', () =>
                saveEventOverride(eventKey, body),
            ),
        [runSave],
    );

    const handleEventReset = useCallback(
        (eventKey) =>
            runSave('Event override reset', 'reset_event_override', () =>
                resetEventOverride(eventKey),
            ),
        [runSave],
    );

    const handleTestSent = useCallback(
        (channel) => {
            // Refetch so channelAvailability flips to available=true once the bot DM exists.
            if (channel === 'gchat') {
                fetchPreferences();
            }
        },
        [fetchPreferences],
    );

    if (loading) {
        return (
            <div className="wrapper-grey notification-preferences">
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        );
    }

    if (initialLoadError) {
        return (
            <div className="wrapper-grey notification-preferences">
                <Alert
                    type="error"
                    showIcon
                    message="Couldn't load notification preferences"
                    description={initialLoadError}
                />
            </div>
        );
    }

    if (!data || !Array.isArray(data.categories)) {
        // Contract failure: 200 response missing the categories array.
        logger.error(
            '[NotificationPreferences] API response missing `categories` array — backend contract violation.',
        );
        return (
            <div className="wrapper-grey notification-preferences">
                <Alert
                    type="error"
                    showIcon
                    message="Notification preferences are unavailable"
                    description="The server didn't return the expected data. Please reload, or contact support if it persists."
                />
            </div>
        );
    }

    if (data.categories.length === 0) {
        return (
            <div className="wrapper-grey notification-preferences">
                <Empty description="No notification categories configured yet." />
            </div>
        );
    }

    return (
        <div className="wrapper-grey notification-preferences">
            <div className="notification-preferences__header">
                <Title level={2} className="notification-preferences__title">
                    NOTIFICATION PREFERENCES
                </Title>
                <Paragraph type="secondary" className="notification-preferences__lede">
                    Choose which channels deliver each kind of notification, and how often you
                    want them grouped.
                </Paragraph>
            </div>

            <ChannelAvailabilityBanner
                channelAvailability={data.channelAvailability}
                onRefresh={fetchPreferences}
            />

            {data.categories.map((category) => (
                <CategoryCard
                    key={category.key}
                    category={category}
                    availableChannels={data.channelAvailability}
                    frequencies={data.frequencies}
                    onCategorySave={handleCategorySave}
                    onEventSave={handleEventSave}
                    onEventReset={handleEventReset}
                    onTestSent={handleTestSent}
                    saving={saving}
                />
            ))}
        </div>
    );
};

export default NotificationPreferences;
